<script setup>
// 每日歡迎彈窗。
//
// 設計稿是一張整圖（插畫背景＋文字＋按鈕），這裡不直接貼那張圖：站上的插畫素材
// （登山客、山丘、雲）本來就有，文字與按鈕用得跟全站同一套字級與品牌橘，
// 換文案、換票數才不必回頭請設計重出一張圖，窄螢幕也才收得漂亮。
//
// 一天只彈一次，進到任何一頁都算「進站」，所以掛在 app.vue 而不是某一頁上。
import hillImg from '~/assets/img/hill.png'
import hikerImg from '~/assets/img/vote-hiker-left.png'
import cloudImg from '~/assets/img/cloud-2.png'
import arrowIcon from '~/assets/img/icon-arrow.png'

// 每天的基本票：四區各一張（後端 BASE_VOTES_PER_ZONE × 四區，見 CLAUDE.md 5.18）。
// 這是「每天都會拿到幾張」的宣告，不是此刻的剩餘票數——彈窗只在當天第一次進站時出現，
// 即時配額請看投票頁的橫幅或會員中心。
const DAILY_VOTES = 4

// 彈過就記在瀏覽器裡，值是彈出那天的日期（本地時區）；換一天自然又會彈
const STORE_KEY = 'daily_vote_popup'

const route = useRoute()
const router = useRouter()
const { lockScroll } = useScrollLock()

const open = ref(false)
// 這次瀏覽階段判斷過了沒：判斷只做一次，之後換幾頁都不會再彈
const settled = ref(false)

function todayKey() {
  // 用本地時區組字串，不用 toISOString()——那會在台灣的凌晨時段回成前一天
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// 第三方登入的回調頁只是中繼站（讀完參數就把人帶走），彈窗蓋上去只會擋住「登入中」
function isBlocked(path) {
  return path.startsWith('/auth/')
}

function tryOpen() {
  if (settled.value || isBlocked(route.path)) return
  settled.value = true

  let last = ''
  try {
    last = localStorage.getItem(STORE_KEY) || ''
  } catch {
    // 無痕模式讀不到：當作今天還沒彈過
  }
  if (last === todayKey()) return

  try {
    localStorage.setItem(STORE_KEY, todayKey())
  } catch {
    // 寫不進去最多下次進站再彈一次，不影響瀏覽
  }
  open.value = true
}

function close() {
  open.value = false
}

function goVote() {
  close()
  router.push('/trails')
}

function onKeydown(e) {
  if (e.key === 'Escape') close()
}

watch(open, (v) => {
  if (v) {
    window.addEventListener('keydown', onKeydown)
    lockScroll(true)
    return
  }
  window.removeEventListener('keydown', onKeydown)
  lockScroll(false)
})

// 落地頁剛好是回調頁時先不彈，等它把人帶到下一頁再判斷一次
watch(() => route.path, tryOpen)

onMounted(tryOpen)

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="dv">
        <div v-if="open" class="dv-mask" @click.self="close()">
          <div class="dv-panel" role="dialog" aria-modal="true" aria-labelledby="dv-title">
            <!-- 風景是背景，不進無障礙樹 -->
            <div class="dv-scene" aria-hidden="true">
              <img class="dv-cloud dv-cloud-a" :src="cloudImg" alt="" />
              <img class="dv-cloud dv-cloud-b" :src="cloudImg" alt="" />
              <img class="dv-hill" :src="hillImg" alt="" />
              <img class="dv-hiker" :src="hikerImg" alt="" />
            </div>

            <button class="dv-close" type="button" aria-label="關閉" @click="close()">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6 L18 18 M18 6 L6 18" />
              </svg>
            </button>

            <div class="dv-body">
              <p class="dv-kicker">一起為喜愛的步道投下支持！</p>

              <h2 id="dv-title" class="dv-title">
                今日有<b>{{ DAILY_VOTES }}</b
                >張票
              </h2>

              <p class="dv-desc">
                每天都可以獲得 {{ DAILY_VOTES }} 張票，<br />
                為你喜愛的步道投下支持，讓更多人看見臺灣的美！
              </p>

              <!-- 票券張數跟著每日基本票走，改了常數版面自己對得上 -->
              <ul class="dv-tickets">
                <li v-for="i in DAILY_VOTES" :key="i" class="dv-ticket" :style="{ '--i': i - 1 }">
                  <svg class="dv-ticket-art" viewBox="0 0 44 26" aria-hidden="true">
                    <path d="M1 25 L13 4 L23 18 L28 11 L43 25 Z" fill="#8cbb6a" />
                    <path d="M13 4 L23 18 L16 25 L1 25 Z" fill="#5f9642" />
                  </svg>
                  <span class="dv-ticket-line"></span>
                  <span class="dv-ticket-text">VOTE</span>
                </li>
              </ul>

              <button class="dv-cta" type="button" @click="goVote()">
                點我進行投票
                <img class="dv-cta-arrow" :src="arrowIcon" alt="" width="22" height="15" />
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<style scoped>
.dv-mask {
  position: fixed;
  inset: 0;
  /* 壓在登入彈窗（200）之下：兩個都開著時該先讓使用者看見登入 */
  z-index: 190;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.5);
}

.dv-panel {
  position: relative;
  width: 100%;
  max-width: 560px;
  max-height: calc(100vh - 48px);
  overflow: hidden auto;
  border-radius: 28px;
  /* 天色由上而下：淡藍綠 → 草地 */
  background: linear-gradient(180deg, #f7fbf3 0%, #eef7e7 46%, #e2f0d8 100%);
  box-shadow: 0 28px 70px rgba(var(--site-shadow-rgb), 0.32);
  font-family: var(--site-font);
  text-align: center;
}

/* ---------------- 背景風景 ---------------- */

.dv-scene {
  position: absolute;
  inset: 0;
  pointer-events: none;
  user-select: none;
}

.dv-cloud {
  position: absolute;
  display: block;
  opacity: 0.75;
}

.dv-cloud-a {
  top: 54px;
  left: -18px;
  width: 120px;
}

.dv-cloud-b {
  top: 24px;
  right: -26px;
  width: 92px;
  opacity: 0.55;
}

/* 山丘與登山客一律貼齊彈窗底緣（做法同投票頁橫幅） */
.dv-hill {
  position: absolute;
  right: -10%;
  /* 往下壓一點，山頭才不會頂到按鈕 */
  bottom: -10px;
  display: block;
  width: 88%;
  opacity: 0.88;
}

.dv-hiker {
  position: absolute;
  bottom: 0;
  left: 10px;
  display: block;
  width: 78px;
}

/* ---------------- 關閉 ---------------- */

.dv-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.32);
  cursor: pointer;
  transition: background-color 0.25s;
}

.dv-close:hover {
  background: rgba(0, 0, 0, 0.5);
}

.dv-close svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: #fff;
  stroke-width: 2.4;
  stroke-linecap: round;
}

/* ---------------- 內容 ---------------- */

.dv-body {
  position: relative;
  z-index: 1;
  padding: 44px 36px 40px;
}

/* 兩側的斜線是稿上的手寫裝飾，用 CSS 畫，不另外切圖 */
.dv-kicker {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  margin: 0;
  color: #2f6b34;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 1px;
}

.dv-kicker::before,
.dv-kicker::after {
  content: '';
  width: 18px;
  height: 18px;
  border-right: 3px solid #5f9642;
  border-left: 3px solid #5f9642;
  transform: skewX(-20deg);
}

.dv-title {
  position: relative;
  display: inline-block;
  margin: 10px 0 0;
  padding: 0 6px;
  color: #1f5132;
  font-size: clamp(40px, 8.4vw, 58px);
  font-weight: 900;
  line-height: 1.24;
  letter-spacing: 2px;
}

/* 標題底下的黃色筆刷：壓在字後面，略帶傾斜才像手繪 */
.dv-title::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: 6px;
  left: 0;
  z-index: -1;
  height: 14px;
  border-radius: 999px;
  background: #ffdf7e;
  transform: rotate(-0.8deg);
}

.dv-title b {
  margin: 0 4px;
  color: var(--site-orange);
  font-size: 1.28em;
  font-weight: 900;
}

.dv-desc {
  margin: 14px 0 0;
  color: var(--site-text);
  font-size: 16px;
  line-height: 1.75;
}

/* ---------------- VOTE 票券 ---------------- */

.dv-tickets {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 26px 0 0;
  padding: 0;
  list-style: none;
}

/* 四張票交錯歪一點，像隨手擺上去的；票根兩側的半圓缺口用漸層挖 */
.dv-ticket {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 78px;
  height: 100px;
  border-radius: 12px;
  background:
    radial-gradient(circle at 0 62px, transparent 6px, #fdf7e8 6.5px),
    radial-gradient(circle at 100% 62px, transparent 6px, #fdf7e8 6.5px);
  box-shadow: 0 8px 18px rgba(var(--site-shadow-rgb), 0.16);
  transform: rotate(calc((var(--i) - 1.5) * 4deg));
}

.dv-ticket-art {
  width: 44px;
  height: 26px;
}

.dv-ticket-line {
  width: 46px;
  border-top: 2px dashed #cbbf9c;
}

.dv-ticket-text {
  color: #4f8a3a;
  font-size: 14px;
  font-weight: 900;
  letter-spacing: 1px;
}

/* ---------------- 按鈕 ---------------- */

.dv-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  height: 64px;
  margin: 30px 0 0;
  padding: 0 44px;
  border: 0;
  border-radius: 999px;
  background: var(--site-orange);
  color: #fff;
  font-family: inherit;
  font-size: 24px;
  font-weight: 900;
  letter-spacing: 2px;
  box-shadow: 0 14px 26px rgba(var(--site-orange-rgb), 0.34);
  cursor: pointer;
  transition:
    transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1),
    background-color 0.28s,
    box-shadow 0.28s;
}

.dv-cta:hover {
  background: var(--site-orange-deep);
  transform: translateY(-3px);
  box-shadow: 0 20px 34px rgba(var(--site-orange-rgb), 0.44);
}

.dv-cta-arrow {
  display: block;
  transition: transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.dv-cta:hover .dv-cta-arrow {
  transform: translateX(6px);
}

/* ---------------- 進出場（與登入、投票彈窗同一套） ---------------- */

.dv-enter-active,
.dv-leave-active {
  transition: opacity 0.25s;
}

.dv-enter-from,
.dv-leave-to {
  opacity: 0;
}

.dv-enter-active .dv-panel,
.dv-leave-active .dv-panel {
  transition: transform 0.25s;
}

.dv-enter-from .dv-panel,
.dv-leave-to .dv-panel {
  transform: translateY(16px) scale(0.97);
}

@media (prefers-reduced-motion: reduce) {
  .dv-enter-active,
  .dv-leave-active,
  .dv-enter-active .dv-panel,
  .dv-leave-active .dv-panel,
  .dv-cta,
  .dv-cta-arrow,
  .dv-close {
    transition: none;
  }
}

@media (max-width: 560px) {
  .dv-mask {
    padding: 16px;
  }

  .dv-panel {
    border-radius: 22px;
  }

  /* 上緣留給關閉鈕：版面一窄，上一行的斜線裝飾就會頂到它 */
  .dv-body {
    padding: 50px 20px 30px;
  }

  /* 稿上的斷句是照桌機寬度分的，窄螢幕讓它自己折，才不會落單一個「美！」 */
  .dv-desc br {
    display: none;
  }

  /* 版面一窄，登山客會壓到按鈕，收起來只留山丘 */
  .dv-hiker {
    display: none;
  }

  .dv-cloud-a {
    width: 88px;
  }

  /* 窄螢幕沒有登山客擋著，山丘整座露出來，往下壓才不會頂到按鈕 */
  .dv-hill {
    right: -14%;
    bottom: -18px;
    width: 86%;
  }

  .dv-kicker {
    gap: 10px;
    font-size: 14px;
  }

  .dv-desc {
    font-size: 14px;
  }

  .dv-tickets {
    gap: 6px;
    margin-top: 20px;
  }

  .dv-ticket {
    width: 62px;
    height: 82px;
    gap: 6px;
    background:
      radial-gradient(circle at 0 50px, transparent 5px, #fdf7e8 5.5px),
      radial-gradient(circle at 100% 50px, transparent 5px, #fdf7e8 5.5px);
  }

  .dv-ticket-art {
    width: 36px;
    height: 21px;
  }

  .dv-ticket-line {
    width: 36px;
  }

  .dv-ticket-text {
    font-size: 12px;
  }

  .dv-cta {
    height: 54px;
    margin-top: 24px;
    padding: 0 28px;
    font-size: 19px;
  }
}
</style>
